// EmailModals.tsx — three user-facing email-flow modals, styled with
// the account-flow design language (see ./accountStyle.ts + ./AccountModal.tsx).
//
//   1. VerifyEmailModal           — start_verify → verify
//   2. ChangeEmailModal           — request_change → confirm_change
//   3. ChangePasswordWithGateModal — request_email_code → verify_email_code
//                                    → change_password (with verify_token)
//
// Polish over the v1 implementation:
//   - Refined typography + colour palette (warm cream / terracotta)
//   - Header accent icon per flow (mark_email_read / alternate_email / lock)
//   - Live mm:ss countdown for the verify_token expiry
//   - 30-second resend cool-down so the "send code" button can't be spammed
//   - Subtle inline success indication on the code field once 6 digits typed
//   - Cleaner step transitions — no jarring layout shifts
//
// The forgot-password flow lives in /pages/Login/ForgotPassword.tsx
// because it must work without auth; everything here requires a session.

import React from 'react';
import { useTranslation } from 'react-i18next';

import { Input, Modal as MModal, ModalBody, ModalContent } from '@heroui/react';

import { store, useAppSelector } from '@/app/store';

import {
  startVerifyEmail,
  verifyEmail,
  requestEmailChange,
  confirmEmailChange,
  cancelEmailChange,
  requestChangePasswordCode,
  verifyChangePasswordCode,
  changePasswordGlobal,
  changePasswordLocal,
} from '@/features/logins/emailApi';

import FlexRow from '@/components/Common/FlexRow';
import DigitInput from '@/components/Common/DigitInput';
import Button from '@/components/Common/Button';
import CommonInput from '@/components/Common/Input';

import { AccountModal, AccountModalFooter, AccountButton } from './AccountModal';
import { cs } from './accountStyle';
import { useExpiryCountdown, useResendCooldown } from './useCountdown';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const sixDigitRegex = /^\d{6}$/;
const emailRegex = /^\S+@\S+\.\S+$/;
const RESEND_COOLDOWN_SECONDS = 30;

type CloseFn = () => void;

interface BaseModalProps {
  onClose:	CloseFn;
}

// ---------------------------------------------------------------------------
// CodeInput — segmented 6-box digit entry (same component as the auth-code
// screens) shared across the modals.
// ---------------------------------------------------------------------------

interface CodeInputProps {
  value:		string;
  onChange:		(v: string) => void;
  invalid?:		boolean;
  errorMessage?:	string;
}

const CodeInput = ({ value, onChange, invalid, errorMessage }: CodeInputProps) => {
  return (
    <FlexRow direction="column" align="center" className="gap-[6px]">
      <DigitInput value={value} onChange={onChange} autoFocus />
      {invalid && errorMessage &&
        <FlexRow flex={0} className={cs.errorText}>{errorMessage}</FlexRow>
      }
    </FlexRow>
  );
};

// PasswordField — labeled password input with a show/hide eye toggle.
interface PasswordFieldProps {
  label:			string;
  value:			string;
  onChange:		(v: string) => void;
  show:			boolean;
  onToggleShow:	() => void;
  invalid?:		boolean;
  errorMessage?:	string;
  autoFocus?:		boolean;
}

const PasswordField = ({
  label, value, onChange, show, onToggleShow, invalid, errorMessage, autoFocus,
}: PasswordFieldProps) => {
  return (
    <FlexRow direction="column" className="gap-[6px]">
      <FlexRow className={cs.inputLabel}>{label}</FlexRow>
      <Input
        value={value}
        onValueChange={onChange}
        type={show ? 'text' : 'password'}
        autoComplete="new-password"
        autoFocus={autoFocus}
        isInvalid={invalid}
        errorMessage={errorMessage}
        classNames={{
          inputWrapper: cs.inputWrapper,
          input: cs.inputInput,
        }}
        endContent={
          <span className="material-icons cursor-pointer !text-[#6B6258] text-[20px]"
                onClick={onToggleShow}>
            {show ? 'visibility_off' : 'visibility'}
          </span>
        } />
    </FlexRow>
  );
};

// Password complexity rules — mirrors the server policy (8–15 chars,
// upper + lower + digit + special). Evaluated live so each rule ticks as
// the user types.
const passwordRules = [
  { label: '8–15 characters',     test: (p: string) => p.length >= 8 && p.length <= 15 },
  { label: 'An uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'A lowercase letter',  test: (p: string) => /[a-z]/.test(p) },
  { label: 'A digit',             test: (p: string) => /\d/.test(p) },
  { label: 'A special character', test: (p: string) => /[^A-Za-z0-9\s]/.test(p) },
];

// Cryptographically-strong random int in [0, max). Math.random is unsafe
// for password generation.
function randomInt(max: number): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0] % max;
}

// 12-char password guaranteed to satisfy every rule (one of each required
// class + 8 random, then shuffled).
function generatePassword(): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*-_+=?';
  const all = upper + lower + digits + special;
  const must = [
    upper[randomInt(upper.length)],
    lower[randomInt(lower.length)],
    digits[randomInt(digits.length)],
    special[randomInt(special.length)],
  ];
  const rest = Array.from({ length: 8 }, () => all[randomInt(all.length)]);
  const out = [...must, ...rest];
  for (let i = out.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.join('');
}

// PasswordRules — the live checklist shown under the password fields.
const PasswordRules = ({
  ruleStates, matches,
}: {
  ruleStates: { label: string; ok: boolean }[];
  matches: boolean;
}) => (
  <div className="border-solid border-1 border-[#E8E0D5] rounded-xl p-[12px] bg-[#F5F0E8]">
    <div className="text-[11px] font-bold text-[#6B6258] mb-[6px] uppercase tracking-wider">
      Password must include
    </div>
    <div className="grid grid-cols-2 gap-x-[12px] gap-y-[4px]">
      {ruleStates.map((r) => (
        <div key={r.label} className="flex items-center gap-[6px] text-[12px]">
          <span className={`material-icons text-[14px] ${r.ok ? '!text-[--Primetime-main]' : '!text-[#C9C0B4]'}`}>
            {r.ok ? 'check_circle' : 'radio_button_unchecked'}
          </span>
          <span className={r.ok ? 'text-[#1F1F1F]' : 'text-[#6B6258]'}>{r.label}</span>
        </div>
      ))}
      <div className="flex items-center gap-[6px] text-[12px] col-span-2">
        <span className={`material-icons text-[14px] ${matches ? '!text-[--Primetime-main]' : '!text-[#C9C0B4]'}`}>
          {matches ? 'check_circle' : 'radio_button_unchecked'}
        </span>
        <span className={matches ? 'text-[#1F1F1F]' : 'text-[#6B6258]'}>
          New and confirm passwords match
        </span>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// 1. VerifyEmailModal
// ---------------------------------------------------------------------------

export const VerifyEmailModal = ({ onClose }: BaseModalProps) => {
  const { t } = useTranslation();
  const logins = useAppSelector((state) => state.logins);
  const email = logins.login?.email || '';

  const [step, setStep] = React.useState<'start' | 'code' | 'success'>('start');
  const [code, setCode] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState('');
  const cooldown = useResendCooldown(RESEND_COOLDOWN_SECONDS);

  const sendCode = React.useCallback(async () => {
    setPending(true);
    setErrMsg('');
    try {
      const r = await startVerifyEmail();
      if (r.code === 0) {
        cooldown.arm();
        setStep('code');
        return;
      }
      setErrMsg(r.error || t('email:errors:generic'));
    } catch (_e) {
      setErrMsg(t('email:errors:generic'));
    } finally {
      setPending(false);
    }
  }, [cooldown, t]);

  const submitCode = React.useCallback(async () => {
    if (!sixDigitRegex.test(code)) {
      setErrMsg(t('email:errors:bad_code_format'));
      return;
    }
    setPending(true);
    setErrMsg('');
    try {
      const r = await verifyEmail(code);
      if (r.code === 0) {
        setStep('success');
        store.getLogin();
        setTimeout(onClose, 1500);
        return;
      }
      setErrMsg(r.error || t('email:errors:generic'));
    } catch (_e) {
      setErrMsg(t('email:errors:generic'));
    } finally {
      setPending(false);
    }
  }, [code, t, onClose]);

  // Centered "card" layout: circular icon badge → title → subtitle → the email
  // called out in its own tinted pill → side-by-side actions. The email is
  // deliberately NOT interpolated into the subtitle (that's why this uses
  // verify:send_to / verify:sent_to rather than start_message / code_message) —
  // it reads as the subject of the dialog, so it gets its own emphasis.
  const pill = "w-full bg-[#EDF1F9] rounded-[8px] py-[12px] px-[16px] text-center";

  return (
    <MModal backdrop="opaque"
            defaultOpen={true}
            hideCloseButton={true}
            scrollBehavior="outside"
            classNames={{
              backdrop: "bg-[#0000007f]",
              wrapper: "modalShadow",
              base: "!max-h-none rounded-[16px]",
              body: "!overflow-visible",
            }}
            onClose={onClose}
            radius="lg">
      <ModalContent>
        <ModalBody className="min-w-[440px] max-w-[440px] p-[32px] !overflow-visible">
          <FlexRow direction="column" className="gap-[16px] items-center">

            {/* Circular icon badge */}
            <FlexRow flex={0}
                     justify="center"
                     className="w-[80px] h-[80px] shrink-0 rounded-full bg-[#EDF1F9] items-center">
              <span className="material-icons-outlined !text-[--Primetime-main] text-[36px]">
                { step === 'success' ? 'mark_email_read' : 'mail' }
              </span>
            </FlexRow>

            <h2 className="text-[24px] font-bold text-black m-0 text-center">
              { step === 'success'
                  ? t('email:verify:success_title')
                  : t('email:verify:title') }
            </h2>

            { step === 'success' &&
              <>
                <p className="text-[14px] text-[--Dark-Grey] m-0 text-center leading-[20px]">
                  { t('email:verify:success_message', { email }) }
                </p>
                <FlexRow flex={0} justify="center" className="w-full pt-[4px]">
                  <Button caption={t('modals:buttons:done')}
                          onClick={onClose} />
                </FlexRow>
              </>
            }

            { step !== 'success' &&
              <>
                <p className="text-[14px] text-[--Dark-Grey] m-0 text-center leading-[20px]">
                  { step === 'start'
                      ? t('email:verify:send_to')
                      : t('email:verify:sent_to') }
                </p>

                {/* The address, called out */}
                <div className={pill}>
                  <span className="text-[15px] font-bold text-[--Primetime-main] break-all">
                    { email }
                  </span>
                </div>

                { step === 'code' &&
                  <>
                    <DigitInput value={code}
                                onChange={setCode}
                                disabled={pending}
                                autoFocus />
                    <span onClick={() => { if (cooldown.ready && !pending) sendCode(); }}
                          className={`text-[13px] ${
                            !cooldown.ready
                              ? 'text-[--Error] cursor-default'
                              : pending
                                ? 'text-[--Mid-Grey] cursor-default'
                                : 'text-[--Dark-Grey] cursor-pointer hover:underline'
                          }`}>
                      { cooldown.ready
                          ? t('email:verify:resend')
                          : `${t('email:verify:resend')} ${cooldown.display}` }
                    </span>
                  </>
                }

                { errMsg !== '' &&
                  <div className="w-full bg-[#FDECEA] border border-[--Error] rounded-[8px] p-[10px] flex items-start gap-[6px]">
                    <span className="material-icons !text-[--Error] text-[16px]">error</span>
                    <span className="text-[12px] text-[--Error] leading-[16px]">{ errMsg }</span>
                  </div>
                }

                <FlexRow flex={0} justify="center" className="gap-[12px] w-full pt-[4px]">
                  <Button caption={t('modals:buttons:cancel')}
                          layout="white"
                          onClick={onClose} />
                  { step === 'start' &&
                    <Button caption={pending ? '…' : t('email:verify:send_code')}
                            isDisabled={pending}
                            onClick={sendCode} />
                  }
                  { step === 'code' &&
                    <Button caption={pending ? '…' : t('email:verify:submit')}
                            isDisabled={pending || code.length !== 6}
                            onClick={submitCode} />
                  }
                </FlexRow>
              </>
            }
          </FlexRow>
        </ModalBody>
      </ModalContent>
    </MModal>
  );
};

// ---------------------------------------------------------------------------
// 2. ChangeEmailModal
// ---------------------------------------------------------------------------

export const ChangeEmailModal = ({ onClose }: BaseModalProps) => {
  const { t } = useTranslation();

  // The address being replaced — shown as context so the user can confirm which
  // account they're changing before committing.
  const logins = useAppSelector((state) => state.logins);
  const currentEmail = logins.login?.email || '';

  const [step, setStep] = React.useState<'start' | 'code' | 'success'>('start');
  const [newEmail, setNewEmail] = React.useState('');
  const [currentPwd, setCurrentPwd] = React.useState('');
  const [code, setCode] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState('');
  const cooldown = useResendCooldown(RESEND_COOLDOWN_SECONDS);

  const emailInvalid = React.useMemo(() => {
    return newEmail.length > 0 && !emailRegex.test(newEmail);
  }, [newEmail]);

  // Only complain once the user has LEFT the field. Keying the error off
  // "non-empty and not yet a valid address" lights it up on the very first
  // character ("v" → invalid), which reads as an error while they're still
  // mid-way through typing. A submit-attempt flag wouldn't work here either:
  // the Send button is disabled while the address is invalid, so the user
  // could never trigger it.
  const [emailTouched, setEmailTouched] = React.useState(false);

  const requestChange = React.useCallback(async () => {
    if (emailInvalid || !newEmail || !currentPwd) {
      setErrMsg(t('email:change:invalid_inputs'));
      return;
    }
    setPending(true);
    setErrMsg('');
    try {
      const r = await requestEmailChange(newEmail, currentPwd);
      if (r.code === 0) {
        cooldown.arm();
        setStep('code');
        return;
      }
      setErrMsg(r.error || t('email:errors:generic'));
    } catch (_e) {
      setErrMsg(t('email:errors:generic'));
    } finally {
      setPending(false);
    }
  }, [emailInvalid, newEmail, currentPwd, cooldown, t]);

  const submitConfirm = React.useCallback(async () => {
    if (!sixDigitRegex.test(code)) {
      setErrMsg(t('email:errors:bad_code_format'));
      return;
    }
    setPending(true);
    setErrMsg('');
    try {
      const r = await confirmEmailChange(code);
      if (r.code === 0) {
        setStep('success');
        store.getLogin();
        setTimeout(onClose, 1500);
        return;
      }
      setErrMsg(r.error || t('email:errors:generic'));
    } catch (_e) {
      setErrMsg(t('email:errors:generic'));
    } finally {
      setPending(false);
    }
  }, [code, t, onClose]);

  const cancelAndClose = React.useCallback(async () => {
    if (step === 'code') {
      await cancelEmailChange();
      store.getLogin();
    }
    onClose();
  }, [step, onClose]);

  const inputCn = "px-[16px] py-[12px] rounded-[8px] border-solid border-1 border-[--Mid-Grey] !bg-white";
  const pill = "w-full bg-[#EDF1F9] rounded-[8px] py-[12px] px-[16px] text-center";

  return (
    <MModal backdrop="opaque"
            defaultOpen={true}
            hideCloseButton={true}
            scrollBehavior="outside"
            classNames={{
              backdrop: "bg-[#0000007f]",
              wrapper: "modalShadow",
              base: "!max-h-none rounded-[16px]",
              body: "!overflow-visible",
            }}
            onClose={cancelAndClose}
            radius="lg">
      <ModalContent>
        <ModalBody className="min-w-[440px] max-w-[440px] p-[32px] !overflow-visible">
          <FlexRow direction="column" className="gap-[16px] items-center">

            {/* Circular icon badge */}
            <FlexRow flex={0}
                     justify="center"
                     className="w-[80px] h-[80px] shrink-0 rounded-full bg-[#EDF1F9] items-center">
              <span className="material-icons-outlined !text-[--Primetime-main] text-[36px]">
                { step === 'success' ? 'mark_email_read' : 'alternate_email' }
              </span>
            </FlexRow>

            <h2 className="text-[24px] font-bold text-black m-0 text-center">
              { step === 'success'
                  ? t('email:change:success_title')
                  : t('email:change:title') }
            </h2>

            { step === 'success'
              ? <>
                  <p className="text-[14px] text-[--Dark-Grey] m-0 text-center leading-[20px]">
                    { t('email:change:success_message', { email: newEmail }) }
                  </p>
                  <FlexRow flex={0} justify="center" className="w-full pt-[4px]">
                    <Button caption={t('modals:buttons:done')}
                            onClick={onClose} />
                  </FlexRow>
                </>
              : <>
                  <p className="text-[14px] text-[--Dark-Grey] m-0 text-center leading-[20px]">
                    { step === 'start'
                        ? t('email:change:current_is')
                        : t('email:verify:sent_to') }
                  </p>

                  {/* On 'start' this is the address being REPLACED (so the user
                      can confirm which account they're changing); on 'code' it's
                      the NEW address, so they know which inbox to check. */}
                  { step === 'start' &&
                    <span className="text-[15px] font-bold text-black text-center break-all -mt-[8px]">
                      { currentEmail }
                    </span>
                  }

                  { step === 'code' &&
                    <div className={pill}>
                      <span className="text-[15px] font-bold text-[--Primetime-main] break-all">
                        { newEmail }
                      </span>
                    </div>
                  }

                  { step === 'start' &&
                    <>
                      <CommonInput value={newEmail}
                                   label={t('email:fields:new_email')}
                                   onValueChange={setNewEmail}
                                   onBlur={() => setEmailTouched(true)}
                                   type="email"
                                   autoFocus
                                   autoComplete="off"
                                   isInvalid={emailTouched && emailInvalid}
                                   errorMessage={t('email:errors:invalid_email')}
                                   classNames={{ inputWrapper: inputCn }} />
                      <CommonInput value={currentPwd}
                                   label={t('email:fields:current_password')}
                                   onValueChange={setCurrentPwd}
                                   type="password"
                                   autoComplete="new-password"
                                   classNames={{ inputWrapper: inputCn }} />
                    </>
                  }

                  { step === 'code' &&
                    <>
                      <DigitInput value={code}
                                  onChange={setCode}
                                  disabled={pending}
                                  autoFocus />
                      <span onClick={() => { if (cooldown.ready && !pending) requestChange(); }}
                            className={`text-[13px] ${
                              !cooldown.ready
                                ? 'text-[--Error] cursor-default'
                                : pending
                                  ? 'text-[--Mid-Grey] cursor-default'
                                  : 'text-[--Dark-Grey] cursor-pointer hover:underline'
                            }`}>
                        { cooldown.ready
                            ? t('email:verify:resend')
                            : `${t('email:verify:resend')} ${cooldown.display}` }
                      </span>
                    </>
                  }

                  { errMsg !== '' &&
                    <div className="w-full bg-[#FDECEA] border border-[--Error] rounded-[8px] p-[10px] flex items-start gap-[6px]">
                      <span className="material-icons !text-[--Error] text-[16px]">error</span>
                      <span className="text-[12px] text-[--Error] leading-[16px]">{ errMsg }</span>
                    </div>
                  }

                  <FlexRow flex={0} justify="center" className="gap-[12px] w-full pt-[4px]">
                    <Button caption={t('modals:buttons:cancel')}
                            layout="white"
                            onClick={cancelAndClose} />
                    { step === 'start' &&
                      <Button caption={pending ? '…' : t('email:change:send_code')}
                              isDisabled={pending || emailInvalid || !newEmail || !currentPwd}
                              onClick={requestChange} />
                    }
                    { step === 'code' &&
                      <Button caption={pending ? '…' : t('email:change:submit')}
                              isDisabled={pending || code.length !== 6}
                              onClick={submitConfirm} />
                    }
                  </FlexRow>
                </>
            }
          </FlexRow>
        </ModalBody>
      </ModalContent>
    </MModal>
  );
};

// ---------------------------------------------------------------------------
// 3. ChangePasswordWithGateModal
// ---------------------------------------------------------------------------

interface ChangePasswordModalProps extends BaseModalProps {
  openVerify?:	() => void;
}

export const ChangePasswordWithGateModal = ({
  onClose, openVerify,
}: ChangePasswordModalProps) => {
  const { t } = useTranslation();
  const logins = useAppSelector((state) => state.logins);
  const desc = logins.login;
  const email = desc?.email || '';
  const verified = !!desc?.email_verified;
  const projectId = '';   // global user only here; per-project local users
                          // have their own flow in the project UI

  type Step = 'send' | 'code' | 'password' | 'success';
  const [step, setStep] = React.useState<Step>('send');
  const [code, setCode] = React.useState('');
  const [verifyToken, setVerifyToken] = React.useState('');
  const [expiresAt, setExpiresAt] = React.useState<Date | null>(null);
  const [currentPwd, setCurrentPwd] = React.useState('');
  const [newPwd, setNewPwd] = React.useState('');
  const [confirmPwd, setConfirmPwd] = React.useState('');
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState('');
  const cooldown = useResendCooldown(RESEND_COOLDOWN_SECONDS);
  const countdown = useExpiryCountdown(expiresAt);

  const sendCode = React.useCallback(async () => {
    setPending(true);
    setErrMsg('');
    const r = await requestChangePasswordCode(email);
    setPending(false);
    if (r.code === 0) {
      cooldown.arm();
      setStep('code');
      return;
    }
    setErrMsg(r.error || t('email:errors:generic'));
  }, [email, cooldown, t]);

  const submitCode = React.useCallback(async () => {
    if (!sixDigitRegex.test(code)) {
      setErrMsg(t('email:errors:bad_code_format'));
      return;
    }
    setPending(true);
    setErrMsg('');
    const r = await verifyChangePasswordCode(code);
    setPending(false);
    if (r.errors && r.errors[0]?.code === 0 && r.verify_token) {
      setVerifyToken(r.verify_token);
      if (r.expires_at) setExpiresAt(new Date(r.expires_at));
      setStep('password');
      return;
    }
    const e = r.errors?.[0] ?? { code: -1, error: t('email:errors:generic') };
    setErrMsg(e.error);
  }, [code, t]);

  const ruleStates = React.useMemo(
    () => passwordRules.map((r) => ({ label: r.label, ok: r.test(newPwd) })),
    [newPwd],
  );
  const allRulesPass = ruleStates.every((r) => r.ok);
  const matches = newPwd.length > 0 && newPwd === confirmPwd;
  const pwdFormValid = !!currentPwd && allRulesPass && matches && newPwd !== currentPwd;

  const handleGenerate = React.useCallback(() => {
    const pwd = generatePassword();
    setNewPwd(pwd);
    setConfirmPwd(pwd);
    setShowNew(true);
    setShowConfirm(true);
  }, []);

  const submitPassword = React.useCallback(async () => {
    if (!currentPwd) {
      setErrMsg('Enter your current password.');
      return;
    }
    if (!allRulesPass) {
      setErrMsg('New password does not meet the requirements below.');
      return;
    }
    if (newPwd === currentPwd) {
      setErrMsg('New password must differ from current password.');
      return;
    }
    if (!matches) {
      setErrMsg('Passwords do not match.');
      return;
    }
    if (countdown.expired) {
      setErrMsg(t('email:gate:expired'));
      return;
    }
    setPending(true);
    setErrMsg('');
    const body = {
      current_password:	currentPwd,
      new_password:		newPwd,
      verify_token:		verifyToken,
    };
    const r = projectId
      ? await changePasswordLocal(projectId, body)
      : await changePasswordGlobal(body);
    setPending(false);
    if (r.code === 0) {
      setStep('success');
      setTimeout(onClose, 1500);
      return;
    }
    if (r.code === 8) {
      // verify_token expired / invalid between the code step and submit
      // (10-min TTL on the backend). Send the user back to request a
      // fresh code instead of stranding them on a dead token.
      // (change_password.go wire code 8.)
      setVerifyToken('');
      setCode('');
      setStep('send');
      setErrMsg(r.error || t('email:gate:expired'));
      return;
    }
    // code 9 = rate-limited (too many wrong current-password attempts);
    // code 4/5/6 = wrong-current / weak / same-as-current. All carry a
    // server-supplied message we surface verbatim.
    setErrMsg(r.error || t('email:errors:generic'));
  }, [currentPwd, newPwd, confirmPwd, allRulesPass, matches, verifyToken, projectId, countdown.expired, t, onClose]);

  // Pre-condition gate — no verified email yet, route to verify-first.
  if (!verified || !email) {
    return (
      <AccountModal
        title={t('email:gate:title')}
        subtitle={email
          ? t('email:gate:requires_verified', { email })
          : t('email:gate:requires_email')}
        onClose={onClose}>
        <AccountModalFooter>
          <AccountButton variant="ghost" onClick={onClose}>
            {t('modals:buttons:cancel')}
          </AccountButton>
          <AccountButton onClick={() => { onClose(); openVerify?.(); }}>
            {t('email:gate:verify_first')}
          </AccountButton>
        </AccountModalFooter>
      </AccountModal>
    );
  }

  if (step === 'success') {
    return (
      <AccountModal
        title={t('email:gate:success_title')}
        subtitle={t('email:gate:success_message')}
        onClose={onClose}
        headerAccent="check"
        success>
        <AccountModalFooter>
          <AccountButton onClick={onClose}>
            {t('modals:buttons:done')}
          </AccountButton>
        </AccountModalFooter>
      </AccountModal>
    );
  }

  return (
    <AccountModal
      title={t('email:gate:title')}
      subtitle={
        step === 'send'    ? t('email:gate:send_message', { email })
        : step === 'code'  ? t('email:gate:code_message', { email })
        : undefined
      }
      onClose={onClose}>

      {step === 'code' &&
        <FlexRow direction="column" className="gap-[12px]">
          <CodeInput value={code} onChange={setCode}
                     invalid={errMsg !== ''}
                     errorMessage={errMsg} />
          <FlexRow className="pt-[4px] !justify-between">
            <AccountButton variant="text"
                          disabled={!cooldown.ready || pending}
                          onClick={() => { sendCode(); }}
                          suffix={cooldown.ready ? undefined : cooldown.display}>
              {t('email:verify:resend')}
            </AccountButton>
            <span className={cs.caption}>{t('email:verify:check_spam')}</span>
          </FlexRow>
        </FlexRow>
      }

      {step === 'password' &&
        <FlexRow direction="column" className="gap-[16px]">
          <PasswordField label={t('email:fields:current_password')}
                         value={currentPwd}
                         onChange={setCurrentPwd}
                         show={showCurrent}
                         onToggleShow={() => setShowCurrent(!showCurrent)}
                         autoFocus />
          <PasswordField label={t('email:fields:new_password')}
                         value={newPwd}
                         onChange={setNewPwd}
                         show={showNew}
                         onToggleShow={() => setShowNew(!showNew)} />
          <PasswordField label={t('email:fields:confirm_password')}
                         value={confirmPwd}
                         onChange={setConfirmPwd}
                         show={showConfirm}
                         onToggleShow={() => setShowConfirm(!showConfirm)}
                         invalid={confirmPwd.length > 0 && !matches}
                         errorMessage={t('email:errors:password_mismatch')} />

          <FlexRow flex={0} justify="end">
            <span onClick={handleGenerate}
                  className="text-[--Primetime-main] text-[12px] cursor-pointer hover:underline flex items-center gap-[4px]">
              <span className="material-icons text-[14px] !text-[--Primetime-main]">auto_awesome</span>
              {t('email:gate:generate')}
            </span>
          </FlexRow>

          <PasswordRules ruleStates={ruleStates} matches={matches} />

          {errMsg !== '' &&
            <FlexRow className={cs.errorText}>{errMsg}</FlexRow>
          }

          {expiresAt && !countdown.expired &&
            <FlexRow flex={0} justify="center" className="gap-[6px] text-[12px] text-[#6B6258]">
              <span className="material-icons text-[14px] !text-current">schedule</span>
              <span>{t('email:gate:expires_in', { time: countdown.display })}</span>
            </FlexRow>
          }
          {countdown.expired &&
            <FlexRow justify="center" className={cs.errorText}>{t('email:gate:expired')}</FlexRow>
          }
        </FlexRow>
      }

      {step === 'send' && errMsg !== '' &&
        <FlexRow className={cs.errorText}>{errMsg}</FlexRow>
      }

      <AccountModalFooter>
        <AccountButton variant="ghost" onClick={onClose}>
          {t('modals:buttons:cancel')}
        </AccountButton>
        {step === 'send' &&
          <AccountButton disabled={pending} onClick={sendCode}>
            {t('email:gate:send_code')}
          </AccountButton>
        }
        {step === 'code' &&
          <AccountButton disabled={pending || code.length !== 6}
                        onClick={submitCode}>
            {t('email:gate:verify_code')}
          </AccountButton>
        }
        {step === 'password' &&
          <AccountButton disabled={pending || !pwdFormValid || countdown.expired}
                        onClick={submitPassword}>
            {t('email:gate:submit')}
          </AccountButton>
        }
      </AccountModalFooter>
    </AccountModal>
  );
};
